import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  FileText,
  Calendar,
  Award,
  Clock,
  Bell,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Search,
  User,
  MessageSquare,
  Library,
  BarChart3,
  Home,
  UserCheck,
  Megaphone,
  CreditCard
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';

// Role-based navigation configuration
const getNavigationItems = (role) => {
  const baseItems = [
    { text: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' }
  ];

  switch (role) {
    case 'student':
      return [
        ...baseItems,
        { text: 'My Subjects', icon: BookOpen, path: '/dashboard/classes' },
        { 
          text: 'Assignments', 
          icon: FileText, 
          children: [
            { text: 'All Assignments', icon: FileText, path: '/dashboard/assignments' },
            { text: 'My Submissions', icon: FileText, path: '/dashboard/assignments/submissions' },
          ]
        },
        { 
          text: 'Exams', 
          icon: Calendar, 
          children: [
            { text: 'Upcoming Exams', icon: Calendar, path: '/dashboard/exams' },
            { text: 'Results', icon: Award, path: '/dashboard/exams/results' },
          ]
        },
        { text: 'Grades & Reports', icon: Award, path: '/dashboard/grades' },
        { text: 'Attendance', icon: UserCheck, path: '/dashboard/attendance' },
        { text: 'Library Resources', icon: Library, path: '/dashboard/library' },
        { text: 'Messages', icon: MessageSquare, path: '/dashboard/messages' },
        { text: 'Timetable', icon: Clock, path: '/dashboard/timetable' },
        { text: 'Announcements', icon: Megaphone, path: '/dashboard/announcements' },
      ];

    case 'teacher':
      return [
        ...baseItems,
        { text: 'My Classes', icon: BookOpen, path: '/dashboard/teacher-classes' },
        { text: 'Students', icon: Users, path: '/dashboard/students' },
        { 
          text: 'Assignments', 
          icon: FileText, 
          children: [
            { text: 'Create Assignment', icon: FileText, path: '/dashboard/assignments/create' },
            { text: 'Grade Submissions', icon: Award, path: '/dashboard/assignments/grade' },
            { text: 'All Assignments', icon: FileText, path: '/dashboard/assignments' },
          ]
        },
        { 
          text: 'Exams', 
          icon: Calendar, 
          children: [
            { text: 'Create Exam', icon: Calendar, path: '/dashboard/exams/create' },
            { text: 'Schedule Exam', icon: Clock, path: '/dashboard/exams/schedule' },
            { text: 'Grade Exams', icon: Award, path: '/dashboard/exams/grade' },
          ]
        },
        { text: 'Attendance', icon: UserCheck, path: '/dashboard/attendance' },
        { text: 'Gradebook', icon: Award, path: '/dashboard/grades' },
        { text: 'Messages', icon: MessageSquare, path: '/dashboard/messages' },
        { text: 'Resources', icon: Library, path: '/dashboard/library' },
        { text: 'Reports', icon: BarChart3, path: '/dashboard/reports' },
      ];

    case 'parent':
      return [
        ...baseItems,
        { text: 'My Children', icon: Users, path: '/dashboard/manage-children' },
        { text: 'Academic Progress', icon: BarChart3, path: '/dashboard/progress' },
        { text: 'Attendance', icon: UserCheck, path: '/dashboard/attendance' },
        { text: 'Grades & Reports', icon: Award, path: '/dashboard/grades' },
        { text: 'Messages', icon: MessageSquare, path: '/dashboard/messages' },
        { text: 'Events & Announcements', icon: Megaphone, path: '/dashboard/announcements' },
        { text: 'Fee Payments', icon: CreditCard, path: '/dashboard/payments' },
        { text: 'Library Access', icon: Library, path: '/dashboard/library' },
      ];

    case 'staff':
      return [
        ...baseItems,
        { text: 'Student Management', icon: Users, path: '/dashboard/users' },
        { text: 'Assigned Classes', icon: GraduationCap, path: '/dashboard/assigned-classes' },
        { text: 'Assigned Subjects', icon: BookOpen, path: '/dashboard/assigned-subjects' },
        { text: 'Student Results', icon: Award, path: '/dashboard/student-results' },
        { text: 'Timetable', icon: Clock, path: '/dashboard/timetable' },
        { text: 'Reports', icon: BarChart3, path: '/dashboard/staff-reports' },
        { text: 'Messages', icon: MessageSquare, path: '/dashboard/messages' },
        { text: 'Announcements', icon: Megaphone, path: '/dashboard/announcements' },
        { text: 'Events', icon: Calendar, path: '/dashboard/events' },
      ];

    default:
      return baseItems;
  }
};

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  // Disabled: Notification polling
  // const { unreadCount, refreshUnreadCount } = useUnreadCount();
  const unreadCount = 0; // Static value since polling is disabled
  const refreshUnreadCount = () => {}; // No-op function
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const menuItems = getNavigationItems(user?.role);

  const handleMenuToggle = (menuText) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuText]: !prev[menuText]
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0) return 'Dashboard';
    
    // Handle exam routes specially
    if (pathSegments.includes('exams') && pathSegments.includes('take')) {
      return 'Take Exam';
    }
    if (pathSegments.includes('exams') && pathSegments.includes('results')) {
      return 'Exam Results';
    }
    
    const lastSegment = pathSegments[pathSegments.length - 1];
    return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/([A-Z])/g, ' $1');
  };

  const Sidebar = ({ className }) => (
    <div className={cn("h-full bg-white border-r border-gray-200 flex flex-col", className)}>
      {/* Logo */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Bridge of Life</h1>
            <p className="text-sm text-gray-600 capitalize">{user?.role} Portal</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const hasChildren = item.children && item.children.length > 0;
          const isOpen = openMenus[item.text];
          const isActive = location.pathname === item.path;

          return (
            <div key={item.text}>
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className={cn(
                  "w-full justify-start",
                  isActive && "bg-blue-50 text-blue-700 hover:bg-blue-100"
                )}
                onClick={() => {
                  if (hasChildren) {
                    handleMenuToggle(item.text);
                  } else {
                    handleNavigation(item.path);
                  }
                }}
              >
                <Icon className="w-4 h-4 mr-3" />
                <span className="flex-1 text-left">{item.text}</span>
                {hasChildren && (
                  isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                )}
              </Button>

              {hasChildren && isOpen && (
                <div className="ml-4 mt-2 space-y-1">
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const isChildActive = location.pathname === child.path;

                    return (
                      <Button
                        key={child.text}
                        variant={isChildActive ? "secondary" : "ghost"}
                        size="sm"
                        className={cn(
                          "w-full justify-start",
                          isChildActive && "bg-blue-50 text-blue-700 hover:bg-blue-100"
                        )}
                        onClick={() => handleNavigation(child.path)}
                      >
                        <ChildIcon className="w-4 h-4 mr-3" />
                        {child.text}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-gray-200">
        <div className="relative">
          <Button
            variant="ghost"
            className="w-full justify-start p-3 h-auto"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <Avatar className="w-8 h-8 mr-3">
              {user?.profileImage && (
                <AvatarImage src={user.profileImage} alt={`${user.firstName} ${user.lastName}`} />
              )}
              <AvatarFallback className="bg-blue-600 text-white text-sm font-medium">
                {user?.firstName?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-gray-900">{user?.firstName || 'User'}</p>
              <p className="text-xs text-gray-500">{user?.email || 'user@school.com'}</p>
            </div>
            <ChevronDown className="w-4 h-4" />
          </Button>

          {userMenuOpen && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg">
              <div className="p-2 space-y-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={() => handleNavigation('/dashboard/profile')}
                >
                  <User className="w-4 h-4 mr-3" />
                  Profile
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={() => handleNavigation('/dashboard/settings')}
                >
                  <Settings className="w-4 h-4 mr-3" />
                  Settings
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 flex h-screen">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="lg:ml-64 flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 py-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden mr-4"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </Button>
              
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {getPageTitle()}
                </h1>
                <p className="text-sm text-gray-600">
                  Welcome back, {user?.firstName || 'User'}!
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Notifications */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative"
                onClick={() => {
                  navigate('/dashboard/notifications');
                  refreshUnreadCount();
                }}
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-[3px] bg-red-500 text-white text-[10px] leading-[14px] rounded-full text-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>

              {/* User Menu (Desktop) */}
              <div className="hidden lg:block">
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <Avatar className="w-8 h-8">
                    {user?.profileImage && (
                      <AvatarImage src={user.profileImage} alt={`${user.firstName} ${user.lastName}`} />
                    )}
                    <AvatarFallback className="bg-blue-600 text-white text-sm font-medium">
                      {user?.firstName?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{user?.firstName || 'User'}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Breadcrumbs */}
        <div className="bg-gray-50 px-4 py-2 lg:px-6 border-b border-gray-200 flex-shrink-0">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Button variant="ghost" size="sm" onClick={() => handleNavigation(`/dashboard/${user?.role || 'student'}`)}>
                  <Home className="w-4 h-4 mr-1" />
                  Dashboard
                </Button>
              </li>
              {location.pathname !== '/dashboard' && (
                <>
                  <li className="text-gray-400">/</li>
                  <li>
                    <span className="text-sm text-gray-600">{getPageTitle()}</span>
                  </li>
                </>
              )}
            </ol>
          </nav>
        </div>

        {/* Page Content */}
        <main className="p-4 lg:p-6 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;